import React, { useContext, useState } from 'react';
import {
  initialValues,
  validationSchema,
} from './config/createWorkOrderFormConfig';
import './WorkOrderForm.css';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import ReportIssueError from '../../../../reportissue/components/ReportIssueError';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AppInfoContext, UserContext } from '../../../../../../App';

import { ClipLoader } from 'react-spinners';
import FormikCustomSelect from '../../reportproblem/components/FormikCustomSelect';
import Department from './selectoptions/Department';
import DateTimePickerForm from '../date-time/DateTimePickerForm';
import Priority from './selectoptions/Priority';

const fetchProblemDetails = async (id, token) => {
  const apiUrl = process.env.REACT_APP_BACKEND_API;

  const response = await fetch(`${apiUrl}/problems/${id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  return response.json();
};

export default function WorkOrderForm({ setFormInfo, increaseFormStep }) {
  const { userDetails } = useContext(UserContext);
  const { appInformation } = useContext(AppInfoContext);

  const token = localStorage.getItem('token');
  const problemId = appInformation.problemManagement.id;

  const apiUrl = process.env.REACT_APP_BACKEND_API;

  const { data } = useQuery(
    ['problemDatils', problemId],
    () => fetchProblemDetails(problemId, token),
    {
      enabled: !!problemId,
    }
  );
  const [submittingForm, setSubmittingForm] = useState(false);

  const reloadData = () => {
    const currentZoom = window.map.getView().getZoom();
    window.map.getView().setZoom(currentZoom + 0.0001);
  };

  const onSubmit = (values, onSubmitProps) => {
    setSubmittingForm(true);
    reportProblemMutate(values);
  };

  const { mutate: reportProblemMutate } = useMutation({
    mutationFn: (problemData) =>
      fetch(`${apiUrl}/work-orders`, {
        method: 'POST',
        body: JSON.stringify({
          ...problemData,
          note: problemData.note === '' ? null : problemData.note,
          userDepartment: userDetails.department,
          user: userDetails,
          problem: data,
        }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }).then((res) => res.json()),

    onSuccess: (response) => {
      if (
        (response.statusCode < 300 && response.statusCode > 199) ||
        !response.statusCode
      ) {
        setSubmittingForm(false);
        window.popup.hide();
        setFormInfo((prevValue) => ({
          ...prevValue,
          workOrder: { formType: 'open', formSubmissionAccepted: true },
        }));
        increaseFormStep();
        reloadData();
        return;
      }
      setFormInfo((prevValue) => ({
        ...prevValue,
        workOrder: { formType: 'open', formSubmissionAccepted: false },
      }));
      window.popup.hide();
      increaseFormStep();
    },
    onError: (error) => {
      console.log('error', error);
      setFormInfo((prevValue) => ({
        ...prevValue,
        workOrder: { formType: 'open', formSubmissionAccepted: false },
      }));
      setSubmittingForm(false);
      window.popup.hide();
      increaseFormStep();
    },
  });

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        validateOnChange={false}
      >
        {(formik) => {
          const fieldValidation = (fieldName) => {
            formik.validateField(fieldName);
          };

          const convertSelectValueToNumber = (fieldName, e) => {
            formik.setFieldValue(fieldName, parseInt(e.target.value, 10));
          };

          const setFormDate = (dateTimePickerField, date) => {
            formik.setFieldValue(dateTimePickerField, date);
          };

          return (
            <Form>
              <div>
                <div className="work-order-form-body">
                  <div>
                    <FormikCustomSelect
                      selectLabel={'Služba izvršenja'}
                      selectName={'executionDepartment'}
                      selectList={Department}
                      validateSelection={() =>
                        fieldValidation('executionDepartment')
                      }
                      convertSelectValueToNumber={convertSelectValueToNumber}
                    />

                    <DateTimePickerForm
                      dateTimePickerLabel={'Planirani period početka radova'}
                      dateTimePickerName={'startDate'}
                      dateValue={formik.values.startDate}
                      setFormDate={setFormDate}
                      validateDate={() => fieldValidation('startDate')}
                    />

                    <DateTimePickerForm
                      dateTimePickerLabel={'Planirani period završetka radova'}
                      dateTimePickerName={'endDate'}
                      dateValue={formik.values.endDate}
                      setFormDate={setFormDate}
                      validateDate={() => fieldValidation('endDate')}
                    />

                    <div className="wo-form-field-container">
                      <label htmlFor="note" className="wo-form-label">
                        Napomena
                      </label>
                      <Field
                        as="textarea"
                        id="note"
                        name="note"
                        className="work-order-form-field wo-form-textarea"
                      />
                      <ErrorMessage name="note" component={ReportIssueError} />
                    </div>
                  </div>

                  <div>
                    <FormikCustomSelect
                      selectLabel={'Prioritet'}
                      selectName={'priority'}
                      selectList={Priority}
                      validateSelection={() => fieldValidation('priority')}
                      convertSelectValueToNumber={convertSelectValueToNumber}
                    />

                    <div>
                      <label
                        htmlFor="description"
                        className="wo-form-label wo-form-field-container"
                      >
                        Opis radova
                      </label>
                      <Field
                        as="textarea"
                        id="description"
                        name="description"
                        className="work-order-form-field wo-form-textarea wo-work-details"
                      />
                      <ErrorMessage
                        name="description"
                        component={ReportIssueError}
                      />
                    </div>

                    <div className="work-order-button-container  problem-details-mobile">
                      <button
                        type="submit"
                        className="work-order-button"
                        disabled={submittingForm}
                      >
                        {submittingForm ? (
                          <ClipLoader
                            color={'white'}
                            loading={submittingForm}
                            size={20}
                            data-testid="loader"
                          />
                        ) : (
                          'Otvori radni nalog'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="work-order-button-container work-order-buttons-desktop">
                  <button
                    type="submit"
                    className="work-order-button"
                    disabled={submittingForm}
                  >
                    {submittingForm ? (
                      <ClipLoader
                        color={'white'}
                        loading={submittingForm}
                        size={20}
                        data-testid="loader"
                      />
                    ) : (
                      'Otvori radni nalog'
                    )}
                  </button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
